import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/admin/Layout';
import axios from '../../../utils/axios';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../../../context/LanguageContext';
import { formatCurrency } from '../../../utils/formatters';
import OrderFilters from '../../../components/admin/orders/OrderFilters';
import OrdersTable from '../../../components/admin/orders/OrdersTable';
import OrderDetailsModal from '../../../components/admin/orders/OrderDetailsModal';
import EditOrderModal from '../../../components/admin/orders/EditOrderModal';
import OrderSummary from '../../../components/admin/orders/OrderSummary';
import OrderActions from '../../../components/admin/orders/OrderActions';
import Pagination from '../../../components/admin/common/Pagination';

const OrdersIndex = () => {
    const { language, t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [editingOrder, setEditingOrder] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        links: []
    });
    const [summary, setSummary] = useState({
        total_orders: 0,
        cash_total: 0,
        card_total: 0,
        returned_orders: 0
    });

    // Bulk upload state
    const [bulkDate, setBulkDate] = useState('');
    const [bulkUploading, setBulkUploading] = useState(false);

    // Helper function to format date for datetime-local input (currently unused)
    // const formatDateForInput = (date) => {
    //     return date.toISOString().slice(0, 16); // Format as YYYY-MM-DDTHH:MM
    // };

    const [filters, setFilters] = useState({
        start_datetime: '',
        end_datetime: '',
        order_type: '',  // Changed from payment_method to order_type
        status: '',
        order_number: '',
        page: 1,
        perPage: 10
    });
    
    // Separate state to track which filters are actually applied
    const [appliedFilters, setAppliedFilters] = useState({
        start_datetime: '',
        end_datetime: '',
        order_type: '',
        status: '',
        order_number: '',
        page: 1,
        perPage: 10
    });

    // Separate the filter formatting logic from the fetch operation
    const formatFilters = (filterObj, page = 1) => {
        const formattedFilters = { ...filterObj, page, perPage: filterObj.perPage };
        
        // Format the datetime values for the backend - simple and direct approach
        if (formattedFilters.start_datetime) {
            // Convert HTML datetime-local input format (YYYY-MM-DDTHH:MM) to MySQL format (YYYY-MM-DD HH:MM:SS)
            formattedFilters.start_datetime = formattedFilters.start_datetime.replace('T', ' ') + ':00';
            formattedFilters.start_date = formattedFilters.start_datetime.split(' ')[0];
            

        }
        
        if (formattedFilters.end_datetime) {
            // Convert HTML datetime-local input format (YYYY-MM-DDTHH:MM) to MySQL format (YYYY-MM-DD HH:MM:SS)
            formattedFilters.end_datetime = formattedFilters.end_datetime.replace('T', ' ') + ':00';
            formattedFilters.end_date = formattedFilters.end_datetime.split(' ')[0];
            

        }
        
        // Filter out empty values
        return Object.fromEntries(
            Object.entries(formattedFilters).filter(([_, value]) => value !== '')
        );
    };

    const fetchOrders = useCallback(async (page = 1, filterObj = appliedFilters) => {
        try {
            setLoading(true);
            // Format dates properly for API request
            const params = formatFilters(filterObj, page);
            

            
            // First get the paginated orders for display
            const response = await axios.get('orders', { params });
            
            // Handle the paginated response
            const { data, meta } = response.data;
            setOrders(data || []);
            
            // Update pagination state while preserving the per_page value
            setPagination({
                current_page: meta.current_page,
                last_page: meta.last_page,
                total: meta.total,
                links: meta.links || [],
                from: meta.from,
                to: meta.to,
                per_page: meta.per_page || filterObj.perPage // Ensure per_page is always set
            });
            
            // Fetch summary with the same filters as the orders list
            const summaryParams = formatFilters(filterObj, 0);
            summaryParams.perPage = 9999;
            summaryParams.summary_only = true;

            const summaryResponse = await axios.get('orders/summary', { params: summaryParams });
            const summaryData = summaryResponse.data;
            
            // Set the summary with data from all orders, not just the current page
            setSummary({
                total_orders: summaryData.total_orders ?? 0,
                cash_total: summaryData.cash_total ?? 0,
                card_total: summaryData.card_total ?? 0,
                returned_orders: summaryData.returned_orders ?? 0
            });
            
            
        } catch (error) {
            toast.error(t('admin:fetch_orders_error') || 'Error fetching orders');
            console.error('Error fetching orders:', error);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]); // Intentionally omitting filters to prevent auto-fetching

    // Initial load of orders when component mounts
    useEffect(() => {
        // Load orders without any filters initially
        fetchOrders(1, {
            start_datetime: '',
            end_datetime: '',
            order_type: '',
            status: '',
            order_number: '',
            page: 1,
            perPage: filters.perPage
        });
    }, [fetchOrders, filters.perPage]);
    
    // Removed auto-fetch on page change to avoid skipping page 1 and double fetches. We'll fetch explicitly in handlePageChange.

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        // No automatic fetch here - wait for search button click
    };
    
    const handleSearch = (e) => {
        e.preventDefault();
        // Reset to page 1 when searching
        const updatedFilters = { ...filters, page: 1 };
        // Update the applied filters with current filter values
        setAppliedFilters(updatedFilters);
        // Keep the filter form values
        setFilters(updatedFilters);
        fetchOrders(1, updatedFilters);
    };
    
    const handlePageChange = (page) => {
        setFilters(prev => ({ ...prev, page }));
        // Explicitly fetch for the selected page using last applied filters (not form state)
        fetchOrders(page, { ...appliedFilters, page });
    };
    
    const handlePerPageChange = async (perPage) => {
        // When changing per page, reset to first page
        const newFilters = { ...filters, perPage, page: 1 };
        
        // Update pagination state immediately to reflect the new per_page value
        setPagination(prev => ({
            ...prev,
            per_page: perPage,
            current_page: 1
        }));
        
        // Set filters and then manually fetch orders to ensure synchronization
        await setFilters(newFilters);
        
        // Directly call the API with the updated perPage value
        try {
            setLoading(true);
            
            // Format the datetime strings properly for the backend
            const formattedFilters = { ...newFilters, page: 1 };
            
            if (formattedFilters.start_datetime) {
                // Convert HTML datetime-local input format (YYYY-MM-DDTHH:MM) to MySQL format (YYYY-MM-DD HH:MM:SS)
                formattedFilters.start_datetime = formattedFilters.start_datetime.replace('T', ' ') + ':00';
                formattedFilters.start_date = formattedFilters.start_datetime.split(' ')[0];
                
        
            }
            
            if (formattedFilters.end_datetime) {
                // Convert HTML datetime-local input format (YYYY-MM-DDTHH:MM) to MySQL format (YYYY-MM-DD HH:MM:SS) 
                formattedFilters.end_datetime = formattedFilters.end_datetime.replace('T', ' ') + ':00';
                formattedFilters.end_date = formattedFilters.end_datetime.split(' ')[0];
                
        
            }
            
            // Filter out empty values
            const params = Object.fromEntries(
                Object.entries(formattedFilters).filter(([_, value]) => value !== '')
            );
            
            // Get the paginated orders with the new perPage value
            const response = await axios.get('orders', { params });
            
            const { data, meta } = response.data;
            setOrders(data || []);
            
            // Update pagination state with the response from the API
            setPagination({
                current_page: meta.current_page,
                last_page: meta.last_page,
                total: meta.total,
                links: meta.links || [],
                from: meta.from,
                to: meta.to,
                per_page: perPage // Explicitly set to the selected value
            });
            
            // Also update the summary with the same filters as the orders list
            const summaryParamsForPageChange = formatFilters(newFilters, 0);
            summaryParamsForPageChange.perPage = 9999;
            summaryParamsForPageChange.summary_only = true;
            const summaryResponse = await axios.get('orders/summary', { params: summaryParamsForPageChange });
            const summaryData = summaryResponse.data;
            
            setSummary({
                total_orders: summaryData.total_orders || meta.total || 0,
                cash_total: summaryData.cash_total || 0,
                card_total: summaryData.card_total || 0,
                returned_orders: summaryData.returned_orders || 0
            });
            
        } catch (error) {
            toast.error(t('admin:fetch_orders_error') || 'Error fetching orders');
            console.error('Error fetching orders:', error);
        } finally {
            setLoading(false);
        }
    };

    // This function is now defined earlier in the file

    const handleClearFilters = () => {
        // Preserve the current perPage setting when clearing filters
        const currentPerPage = filters.perPage;
        
        // Reset the form filters to blank (no default dates)
        setFilters({
            start_datetime: '',
            end_datetime: '',
            order_type: '',  // Changed from payment_method to order_type
            status: '',
            order_number: '', // Use order_number instead of customer fields
            page: 1,
            perPage: currentPerPage // Keep current perPage setting
        });
        
        // Clear the applied filters to show all orders
        setAppliedFilters({
            start_datetime: '',
            end_datetime: '',
            order_type: '',
            status: '',
            order_number: '',
            page: 1,
            perPage: currentPerPage
        });
        
        fetchOrders(1); // Reset to page 1 when clearing filters
    };

    const handleExport = async () => {
        try {
            // Format the filters the same way as when fetching orders
            const params = formatFilters(filters, 1);
            
            // Set perPage to a high number to export all filtered orders
            params.perPage = 9999;
            

            
            const response = await axios.get('orders/export', {
                params: params,
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers['content-disposition'];
            const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/); 
            const filename = filenameMatch ? filenameMatch[1] : 'orders.csv';
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error(t('admin:export_error') || 'Error exporting orders');
            console.error('Error exporting orders:', error);
        }
    };

    const handlePrintInvoice = async (order) => {
        try {
            const orderIdentifier = order.order_number || order.id;

            if (!orderIdentifier) {
                throw new Error('Missing order identifier');
            }

            const response = await axios.get(`orders/${encodeURIComponent(orderIdentifier)}/print`, {
                responseType: 'blob'
            });
            
            // Open the HTML invoice in a new window for printing
            const blob = new Blob([response.data], { type: 'text/html' });
            const url = window.URL.createObjectURL(blob);
            const printWindow = window.open(url, '_blank');
            
            // Wait for the content to load, then trigger print dialog
            if (printWindow) {
                printWindow.onload = function() {
                    setTimeout(() => {
                        printWindow.print();
                    }, 250);
                };
            }
            
            // Clean up the URL after a delay
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 1000);
        } catch (error) {
            toast.error(t('admin:print_error') || 'Error printing invoice');
            console.error('Error printing invoice:', error);
        }
    };

    const handleDeleteOrder = async (order) => {
        if (!window.confirm(t('admin:confirm_delete') || 'Are you sure you want to delete this order?')) {
            return;
        }

        try {
            await axios.delete(`orders/${order.id}`);
            toast.success(t('admin:order_deleted') || 'Order deleted successfully');
            setFilters(prev => ({ ...prev, page: 1 }));
            fetchOrders(1);
        } catch (error) {
            toast.error(t('admin:delete_error') || 'Error deleting order');
            console.error('Error deleting order:', error);
        }
    };

    const handleReturnOrder = async (order) => {
        if (!window.confirm(t('admin:confirm_return') || 'Are you sure you want to return this order?')) {
            return;
        }

        try {
            await axios.post(`orders/${order.id}/return`);
            toast.success(t('admin:order_returned'));
            setFilters(prev => ({ ...prev, page: 1 }));
            fetchOrders(1);
        } catch (error) {
            toast.error(t('admin:return_error'));
            console.error('Error returning order:', error);
        }
    };
    
    // Handle edit order by showing the EditOrderModal
    const handleEditOrder = (order) => {
        try {
            // Log the order being edited for debugging

            
            // Set the order to be edited, which will open the modal
            setEditingOrder(order);
        } catch (error) {
            console.error('Error preparing order for edit:', error);
            toast.error(t('admin:edit_error') || 'Error preparing order for edit');
        }
    };
    
    // Handle order update after editing
    const handleOrderUpdate = async (updatedOrder) => {
        try {
            // Close the edit modal
            setEditingOrder(null);
            
            // Refresh the orders list
            await fetchOrders(filters.page);
            
            // Show success message
            toast.success(t('admin:order_updated'));
        } catch (error) {
            console.error('Error updating order:', error);
            toast.error(t('admin:update_error'));
        }
    };

    const [loadingOrder, setLoadingOrder] = useState(false);

    const handleViewOrder = async (order) => {
        try {
            setLoadingOrder(true);
            
            // Determine if this is a card order based on the order's source table
            // order.is_card_order would be set by the backend when listing orders
            // OR we can determine it from order table type
            let endpoint;
            const orderNumber = order.order_number;
            const isCardOrder = order.is_card_order || order.hasOwnProperty('card_items') || (order.table_name === 'sales_order_card');
            
            if (isCardOrder) {
                // For card orders - use the card orders API endpoint
                endpoint = `sales-order-card/${orderNumber}`;
            } else {
                // For cash orders - use the regular orders API endpoint
                endpoint = `orders/${orderNumber}`;
            }
            

            
            // Fetch the complete order with items
            const response = await axios.get(endpoint);
            const orderData = response.data.data || response.data; // Handle different response formats
            const completeOrder = orderData;
            
            if (!completeOrder) {
                throw new Error('No order data received');
            }
            

            
            // Ensure the order has items array
            if (!completeOrder.items) {
                completeOrder.items = [];
                
                // For cash orders, items are in order_items
                if (completeOrder.order_items && Array.isArray(completeOrder.order_items)) {
                    completeOrder.items = completeOrder.order_items;
                }
                // For card orders, items are in card_items
                else if (completeOrder.card_items && Array.isArray(completeOrder.card_items)) {
                    completeOrder.items = completeOrder.card_items;
                }
            }
            
            // Add a flag to indicate if this is a card order for the UI
            completeOrder.is_card_order = isCardOrder;
            
            // Set the complete order with items to the selected order
            setSelectedOrder(completeOrder);
        } catch (error) {
            toast.error(t('admin:fetch_order_error') || 'Error fetching order details');
            console.error('Error fetching order details:', error);
        } finally {
            setLoadingOrder(false);
        }
    };

    return (
        <Layout>
            <div className="w-full max-w-full mx-auto px-2 sm:px-4 py-4 sm:py-8 pb-24 overflow-x-hidden">
            <OrderActions
                onExport={handleExport}
                onToggleFilters={() => setShowFilters(!showFilters)}
                showFilters={showFilters}
                language={language}
                t={t}
                bulkDate={bulkDate}
                onBulkDateChange={setBulkDate}
                bulkUploading={bulkUploading}
                onBulkUpload={async () => {
                    if (!bulkDate) return;
                    try {
                        setBulkUploading(true);
                        const { data } = await axios.post('orders/zatca/bulk-upload', { date: bulkDate });
                        toast.success((t('admin:bulk_upload_done') || 'Bulk upload completed') + `: ${data.uploaded}/${data.attempted}`);
                        // Refresh orders list to reflect new ZATCA statuses
                        fetchOrders(filters.page, filters);
                    } catch (err) {
                        console.error(err);
                        toast.error(t('admin:bulk_upload_failed') || 'Bulk upload failed');
                    } finally {
                        setBulkUploading(false);
                    }
                }}
            />

            <OrderSummary
                summary={summary}
                language={language}
                t={t}
                formatCurrency={formatCurrency}
            />

            <OrderFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onClear={handleClearFilters}
                language={language}
                t={t}
                isVisible={showFilters}
            />

            <OrdersTable
                orders={orders}
                loading={loading}
                language={language}
                t={t}
                formatCurrency={formatCurrency}
                isAdmin={true}
                canUploadZatca={true}
                onViewOrder={handleViewOrder}
                onPrintInvoice={handlePrintInvoice}
                onEditOrder={handleEditOrder}
                onDeleteOrder={handleDeleteOrder}
                onReturnOrder={handleReturnOrder}
                onUploadZatca={async (order) => {
                    try {
                        const orderType = order.is_card_order ? 'card' : 'cash';
                        const url = `orders/${order.id}/zatca/upload?order_type=${orderType}`;
                        await axios.post(url);
                        toast.success('Uploaded to ZATCA');
                        fetchOrders(filters.page, filters);
                    } catch (e) {
                        console.error(e);
                        toast.error('Failed to upload to ZATCA');
                    }
                }}
            />
            
            {/* Pagination */}
            {!loading && orders.length > 0 && (
                <div className="mt-5">
                    <Pagination 
                        pagination={pagination} 
                        onPageChange={handlePageChange}
                        onPerPageChange={handlePerPageChange}
                    />
                </div>
            )}

            <OrderDetailsModal
                order={selectedOrder}
                isOpen={!!selectedOrder}
                onClose={() => setSelectedOrder(null)}
                language={language}
                t={t}
                formatCurrency={formatCurrency}
                loading={loadingOrder}
            />
            
            {/* Edit Order Modal */}
            {editingOrder && (
                <EditOrderModal
                    order={editingOrder}
                    onClose={() => setEditingOrder(null)}
                    onUpdate={handleOrderUpdate}
                />
            )}
            </div>
        </Layout>
    );
};

export default OrdersIndex;
